<template>
  <LSelect
    :id="fieldId"
    :label="alias || ''"
    :multiple="false"
    :disabled="disabled"
    :errors="error"
    :infos="infoMessage ? [infoMessage] : undefined"
    :options="formattedItems"
    :model-value="modelValueString"
    @update:model-value="handleUpdate"
  >
    <template v-if="appendIcon === 'mdi-information-outline'" #append-icon>
      <LIcon icon="lnx-info" @click="handleModal" />
    </template>
  </LSelect>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { LSelect, LIcon } from '@wgg.hyve/hyve-ui-library'
import { formatNumber } from '@/services/helper.service.ts'

type Item = string | number | { title: string; value: string | number }

interface Props {
  fieldId?: string
  color?: string
  clearable?: boolean
  disabled?: boolean
  rules?: Array<any>
  modelValue?: string | number
  items: Item[]
  alias?: string
  error?: string
  prependIcon?: string
  appendIcon?: string
  modalContent?: string
  infoMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  clearable: false,
  disabled: false,
  items: () => []
})
const { t } = useI18n()

const emit = defineEmits<{
  (e: 'openModal', content: string): void
  (e: 'update:modelValue', value: string | number): void
}>()

const formatItemDisplay = (value: Item): string => {
  if (typeof value === 'object') {
    return t(value.title)
  }
  if (typeof value === 'number') {
    return formatNumber(value)
  }
  return t(value)
}

type Option = { label: string; value: string }
const formattedItems = computed<Option[]>(() =>
  props.items.map((item) => ({ label: formatItemDisplay(item), value: String(typeof item === 'object' ? item.value : item) }))
)

const modelValueString = computed<string | undefined>(() =>
  props.modelValue !== undefined && props.modelValue !== null ? String(props.modelValue) : undefined
)

const handleUpdate = (value: unknown) => {
  const next = Array.isArray(value) ? value[0] : value
  if (next === undefined || next === null) return
  emit('update:modelValue', next as string)
}

const handleModal = () => {
  if (props.modalContent) {
    emit('openModal', props.modalContent)
  }
}
</script>
